import * as React from "react";
import { CardGrid, EmptyState } from "@aisquare/ui";
import { Inbox } from "lucide-react";
import { Section, VariantBlock } from "../lib/Section";

interface Row {
  id: string;
  label: string;
  tag: "learn" | "media" | "doc";
}

// 12 items across 4 columns so the per-row staggered fan-in is actually
// visible (6 items in 3 cols barely shows it).
const LABELS = [
  "Quest",
  "AI Note",
  "Podcast",
  "AI Video",
  "AI Expert",
  "Summary",
  "Flashcards",
  "Roadmap",
  "Glossary",
  "AI Tutor",
  "Snapshot",
  "Mind Map",
];
const TAGS: Row["tag"][] = ["learn", "doc", "media"];
const ROWS: Row[] = LABELS.map((label, i) => ({
  id: String(i + 1),
  label,
  tag: TAGS[i % TAGS.length],
}));

const card = (r: Row) => (
  <div className="rounded-card border border-border bg-card p-4 text-sm text-card-foreground">
    {r.label}
  </div>
);

export function CardGridDemo() {
  // Filter state drives the reflow: changing `transitionKey` replays the
  // staggered fill-from-empty entrance.
  const [tag, setTag] = React.useState<string>("all");
  const filtered = tag === "all" ? ROWS : ROWS.filter((r) => r.tag === tag);

  // Loading toggle shows the crossfade from skeletons to live cards.
  const [loading, setLoading] = React.useState(false);

  // The three-way, which is the pairing that actually gets shipped wrong:
  // `loading` and `empty` are handed the SAME empty array.
  const [phase, setPhase] = React.useState<"loading" | "empty" | "loaded">("loading");

  return (
    <Section
      id="card-grid"
      title="CardGrid"
      description="The generic card-grid engine: a responsive grid rhythm, a per-row staggered spring entrance (each row's cells fan in by column, then the next row restarts its own fan, so the grid settles in one beat), a popLayout reflow, a crossfade from loading skeletons to the real cards, and count-matched skeletons so the layout never collapses while a result set loads. The layout spring + stagger collapse to an instant, opacity-only render under prefers-reduced-motion."
    >
      <VariantBlock
        label="Filter reflow"
        caption="changing the filter replays the per-row staggered entrance via transitionKey"
      >
        <div className="w-full max-w-4xl">
          <div className="mb-3 flex gap-2">
            {["all", "learn", "media", "doc"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTag(t)}
                className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground data-[active=true]:border-primary data-[active=true]:text-primary"
                data-active={tag === t}
              >
                {t}
              </button>
            ))}
          </div>
          <CardGrid<Row>
            items={filtered}
            getKey={(r) => r.id}
            renderItem={card}
            transitionKey={tag}
            columns="4"
          />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Loading -> content"
        caption="toggle to crossfade the default composed card skeletons (image block + two text lines) into the live cards"
      >
        <div className="w-full max-w-4xl">
          <button
            type="button"
            onClick={() => setLoading((v) => !v)}
            className="mb-3 rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {loading ? "show content" : "show loading"}
          </button>
          <CardGrid<Row>
            items={loading ? [] : ROWS}
            getKey={(r) => r.id}
            renderItem={card}
            loading={loading}
            skeletonCount={8}
            columns="4"
          />
        </div>
      </VariantBlock>
      <VariantBlock
        label="Loading -> empty -> content"
        caption="loading and empty are handed the SAME empty array — only the flag separates them, and loading wins. Answering a request still in flight with 'nothing here' invites the user to leave a grid that was one tick from filling"
      >
        <div className="w-full max-w-4xl">
          <div className="mb-3 flex gap-2">
            {(["loading", "empty", "loaded"] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPhase(p)}
                className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground data-[active=true]:border-primary data-[active=true]:text-primary"
                data-active={phase === p}
              >
                {p}
              </button>
            ))}
          </div>
          <CardGrid<Row>
            items={phase === "loaded" ? ROWS : []}
            getKey={(r) => r.id}
            renderItem={card}
            loading={phase === "loading"}
            skeletonCount={8}
            columns="4"
            empty={
              <EmptyState
                icon={<Inbox className="h-6 w-6" />}
                title="Nothing here yet."
                description="First one's the hardest. After that, the rest just show up."
              />
            }
          />
        </div>
      </VariantBlock>
    </Section>
  );
}
